import { Form, FormCancel } from "../components";
import { useFeedback } from "../hooks/useFeedback";
import Icon from "../components/Icon";
import { Toast, PageTitle, EmptyState, Card, FormSection, Btn, ConfirmModal, Input, Modal, Spinner } from "../components";
import { useState, useEffect } from "react";
import { apiFetch } from "../api";

export default function EmpresasDestinoPage({ userRole }) {
  const [empresas, setEmpresas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ nome: "", endereco: "", cliente_id: "" });
  const [submitError, setSubmitError] = useState("");
  const { toast, toastType, showToast, showError, dismissToast } = useFeedback("empresas_destino");
  const [confirmId, setConfirmId] = useState(null);

  const isSuperadmin = userRole === "superadmin";

  useEffect(() => {
    setLoading(true);
    setLoadError("");
    const promises = [apiFetch("/empresas-destino")];
    if (isSuperadmin) promises.push(apiFetch("/clientes-rotaflex"));
    Promise.all(promises)
      .then(([e, c]) => { setEmpresas(e); if (c) setClientes(c); })
      .catch(e => { setLoadError(e.message); showError(e.message); })
      .finally(() => setLoading(false));
  }, [refresh, showError, isSuperadmin]);



  const openNew = () => {
    setSubmitError("");
    setForm({ nome: "", endereco: "", cliente_id: "" });
    setEditItem(null);
    setModal(true);
  };

  const openEdit = (e) => {
    setSubmitError("");
    setForm({ nome: e.nome, endereco: e.endereco || "", cliente_id: e.cliente_id || "" });
    setEditItem(e.id);
    setModal(true);
  };

  const save = async () => {
    if (saving) return;
    setSubmitError("");
    if (!form.nome.trim() || !form.endereco.trim()) { setSubmitError("Preencha nome e endereço completo da empresa."); return; }
    if (isSuperadmin && !editItem && !form.cliente_id) {
      setSubmitError("Selecione o cliente RotaFlex para vincular a empresa destino.");
      return;
    }
    setSaving(true);
    try {
      if (editItem) {
        const res = await apiFetch(`/empresas-destino/${editItem}`, { method: "PUT", body: JSON.stringify(form) });
        setEmpresas(empresas.map(e => e.id === editItem ? { ...e, ...res } : e));
        showToast("Empresa atualizada.");
      } else {
        const body = { nome: form.nome, endereco: form.endereco };
        if (isSuperadmin) body.cliente_id = form.cliente_id || null;
        const res = await apiFetch("/empresas-destino", { method: "POST", body: JSON.stringify(body) });
        const clienteNome = clientes.find(c => c.id === res.cliente_id)?.nome || null;
        setEmpresas([...empresas, { ...res, cliente_nome: clienteNome }]);
        showToast("Empresa cadastrada.");
      }
      setModal(false);
    } catch (e) { setSubmitError(e.message); showError(e.message); }
    finally { setSaving(false); }
  };

  const desativar = async () => {
    try {
      await apiFetch(`/empresas-destino/${confirmId}`, { method: "DELETE" });
      setEmpresas(empresas.map(e => e.id === confirmId ? { ...e, ativo: false } : e));
      setConfirmId(null);
      showToast("Empresa desativada.");
    } catch (e) { showError(e.message); }
  };

  const headers = isSuperadmin
    ? ["Empresa", "Endereço", "Cliente", ""]
    : ["Empresa", "Endereço", ""];
  const colSpan = headers.length;

  return (
    <div className="fade-in">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
        <div>
          <PageTitle icon="building">Empresas de destino</PageTitle>
          <p style={{ color: "var(--muted)", fontSize: '1rem', marginTop: 2 }}>
            Locais atendidos pelas rotas da transportadora · {empresas.filter(e => e.ativo !== false).length} ativas
          </p>
        </div>
        <Btn onClick={openNew}>+ Nova empresa</Btn>
      </div>

      {loadError && <Card style={{ marginBottom: 16 }}><p role="alert" style={{ color: "var(--danger)", marginBottom: 12 }}>{loadError}</p><Btn variant="secondary" onClick={() => setRefresh(value => value + 1)}>Tentar novamente</Btn></Card>}

      <Card style={{ padding: 0, overflowX: "auto" }}>
        <table style={{ width: "100%", minWidth: 600, borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--border)" }}>
              {headers.map(h => (
                <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: '0.875rem', color: "var(--muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={colSpan} style={{ padding: 40, textAlign: "center", color: "var(--muted)" }}><Spinner /></td></tr>
            )}
            {!loading && !loadError && empresas.length === 0 && (
              <tr><td colSpan={colSpan} style={{ padding: 40, textAlign: "center" }}>
                <EmptyState title="Nenhuma empresa de destino cadastrada" icon="building"><p>Cadastre os locais atendidos para utilizá-los nos pedidos e no planejamento.</p></EmptyState>
                <Btn size="sm" onClick={openNew}>+ Nova empresa</Btn>
              </td></tr>
            )}
            {!loading && empresas.map((e, i) => (
              <tr key={e.id}
                style={{ borderBottom: i < empresas.length - 1 ? "1px solid var(--border)" : "none", transition: "background 0.15s", opacity: e.ativo === false ? 0.5 : 1 }}
                onMouseEnter={ev => ev.currentTarget.style.background = "var(--s2)"}
                onMouseLeave={ev => ev.currentTarget.style.background = "transparent"}>
                <td style={{ padding: "12px 16px", fontWeight: 600 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 34, height: 34, background: "rgba(99,102,241,0.12)", border: "1px solid rgba(99,102,241,0.2)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: '1rem', flexShrink: 0 }}><Icon name="building" /></div>
                    {e.nome}
                  </div>
                </td>
                <td style={{ padding: "12px 16px", color: "var(--muted)", fontSize: '0.9375rem', maxWidth: 260 }}>
                  <span style={{ display: "block", overflowWrap: "anywhere" }}>{e.endereco}</span>
                </td>
                {isSuperadmin && (
                  <td style={{ padding: "12px 16px", fontSize: '0.9375rem' }}>
                    {e.cliente_nome
                      ? <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 8px", borderRadius: 6, background: "rgba(249,115,22,0.1)", color: "var(--accent)", fontSize: '0.875rem', fontWeight: 600 }}><Icon name="building" /> {e.cliente_nome}</span>
                      : <span style={{ color: "var(--muted)", fontSize: '0.875rem', fontStyle: "italic" }}>—</span>
                    }
                  </td>
                )}
                <td style={{ padding: "12px 16px" }}>
                  <div style={{ display: "flex", gap: 6 }}>
                    <Btn size="sm" variant="secondary" onClick={() => openEdit(e)}>Editar</Btn>
                    {e.ativo !== false && (
                      <Btn size="sm" variant="danger" onClick={() => setConfirmId(e.id)}>Desativar</Btn>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {modal && (
        <Modal title={editItem ? "Editar empresa" : "Nova empresa de destino"} onClose={() => !saving && setModal(false)}>
          <Form values={form} onSubmit={save} busy={saving} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <p style={{ color: "var(--muted)", fontSize: '0.875rem' }}>Campos com * são obrigatórios.</p>
            <FormSection title="Identificação">
            <Input
              label="Nome da empresa *"
              value={form.nome}
              onChange={e => setForm({ ...form, nome: e.target.value })}
              placeholder="Nome da empresa destino"
            />
            </FormSection>
            <FormSection title="Local de chegada das rotas">
            <Input
              label="Endereço completo *"
              value={form.endereco}
              onChange={e => setForm({ ...form, endereco: e.target.value })}
              placeholder="Av. Brigadeiro Faria Lima, 2170 — São José dos Campos"
            />
            <p style={{ color: "var(--muted)", fontSize: '0.875rem' }}>Inclua rua, número, cidade e estado. Este endereço será usado para levar os passageiros ao destino.</p>
            {isSuperadmin && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label htmlFor="empresa-destino-cliente" style={{ fontSize: '0.9375rem', color: "var(--muted)", fontWeight: 500 }}>Cliente (empresa contratante){!editItem ? " *" : ""}</label>
                <select id="empresa-destino-cliente"
                  value={form.cliente_id}
                  onChange={e => setForm({ ...form, cliente_id: e.target.value })}
                  required={!editItem}
                  style={{ background: "var(--s2)", border: "1px solid var(--border)", borderRadius: 8, padding: "10px 12px", color: "var(--text)", fontSize: '1rem' }}
                >
                  <option value="" disabled>— Selecione o cliente —</option>
                  {clientes.filter(c => c.ativo).map(c => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>
              </div>
            )}
            </FormSection>
            {submitError && <p role="alert" style={{ color: "var(--danger)", fontSize: '0.9375rem', lineHeight: 1.5 }}>{submitError}</p>}
            <div className="rf-form-actions">
              <FormCancel disabled={saving} />
              <Btn type="submit" disabled={saving} >
                {saving ? <><Spinner /> Salvando...</> : "Salvar empresa"}
              </Btn>
            </div>
          </Form>
        </Modal>
      )}

      <ConfirmModal
        open={Boolean(confirmId)}
        title="Desativar empresa"
        message="A empresa será desativada e não aparecerá mais nas opções de destino."
        confirmLabel="Desativar"
        confirmVariant="danger"
        onClose={() => setConfirmId(null)}
        onConfirm={desativar}
      />

      {toast && !(modal && submitError) && (
        <Toast onDismiss={dismissToast} tone={toastType === "error" ? "error" : "success"}>{toast}</Toast>
      )}
    </div>
  );
}
